import { UrlConfig } from 'config';
import { DedupItemModel, RawDedupItemGroup } from 'models/Dedup/DedupItem';
import { DedupListingModel, RawDedupListingData } from 'models/Dedup/DedupListing';
import { ApiResponse } from 'models/Api/ApiResponse';
import { JSONObject } from 'models/Api/types';
import { PaginationModel, RawPaginationData } from 'models/Pagination/Pagination';
import { AjaxService } from 'services';

function buildPaginationModel<TModel, TRaw>(
	response: ApiResponse<RawPaginationData<TRaw>>,
	mapper: (raw: TRaw) => TModel,
): PaginationModel<TModel> {
	const rawPagination = response.data || {};
	const mappedData = rawPagination.data?.map(mapper) || [];
	return new PaginationModel<TModel>({
		...rawPagination,
		data: mappedData,
		fetched: true,
	});
}

export class DedupApiClient {
	// Dedup Items
	public static async getDedupItems(artistId?: string): Promise<DedupItemModel[]> {
		if (artistId) {
			return this.getDedupItemsByArtist(artistId);
		}

		const url = UrlConfig.DEDUP_INTELLIGENCE.DEDUP_ITEMS_ALL;
		const response: ApiResponse<{ groups: RawDedupItemGroup[] }> = await AjaxService.getData(url);
		const groups = response.data?.groups || [];
		return groups.map((group: RawDedupItemGroup) => new DedupItemModel(group));
	}

	private static async getDedupItemsByArtist(artistId: string): Promise<DedupItemModel[]> {
		const url = UrlConfig.DEDUP_INTELLIGENCE.DEDUP_ITEMS_BY_ARTIST(artistId);
		const response: ApiResponse<{ groups: RawDedupItemGroup[] }> = await AjaxService.getData(url);
		const groups = response.data?.groups || [];
		return groups.map((group: RawDedupItemGroup) => new DedupItemModel(group));
	}

	public static async deleteDedupItem(itemId: string): Promise<void> {
		const url = UrlConfig.DEDUP_INTELLIGENCE.DEDUP_ITEM_DELETE(itemId);
		await AjaxService.delData(url);
	}

	public static async deleteDedupItemPair(primaryItemId: string, secondaryItemId: string): Promise<void> {
		const url = UrlConfig.DEDUP_INTELLIGENCE.DEDUP_ITEM_PAIR_DELETE(primaryItemId, secondaryItemId);
		await AjaxService.delData(url);
	}

	// Dedup Listings
	public static async getDedupListings(page: number = 1): Promise<PaginationModel<DedupListingModel>> {
		const url = `${UrlConfig.DEDUP_LISTING.DEDUP_LISTING_ALL}?page=${page}`;
		const response: ApiResponse<RawPaginationData<RawDedupListingData>> = await AjaxService.getData(url);
		return buildPaginationModel(response, (raw) => new DedupListingModel(raw));
	}

	public static async approveDedupListing(id: string): Promise<void> {
		const url = UrlConfig.DEDUP_LISTING.DEDUP_LISTING_APPROVE;
		const payload: JSONObject = { dedup_listing_id: id };
		await AjaxService.postData(url, payload);
	}

	public static async rejectDedupListing(id: string): Promise<void> {
		const url = UrlConfig.DEDUP_LISTING.DEDUP_LISTING_REJECT;
		const payload: JSONObject = { dedup_listing_id: id };
		await AjaxService.postData(url, payload);
	}
}
